import { ChangeDetectionStrategy, Component, inject, input, output, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
	MatDateRangeInput,
	MatDateRangePicker,
	MatDatepickerToggle,
	MatEndDate,
	MatStartDate,
	MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { GenericMatAutocompleteComponent } from '@/common/generic-mat-autocomplete/generic-mat-autocomplete.component';
import { environment } from 'src/environments/environment';
import { Contractor, InternalStatusDto, IssuedByOption, STATUS_VINDICATION_FILTERS } from '../../vindication.model';
import { Result } from '@/reservation/reservation.model';
import { DatePipe } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';

interface InvoiceFiltersFormValue {
	mail?: string;
	nip?: string;
	contractorId?: number;
	number?: string;
	paymentDeadlineFrom?: Date | null;
	paymentDeadlineTo?: Date | null;
	paymentTimeFrom?: Date | null;
	paymentTimeTo?: Date | null;
	declaredPaymentDateFrom?: Date | null;
	declaredPaymentDateTo?: Date | null;
	issueDate?: Date | null;
	status?: string;
	internalStatus?: number[];
	issuedBy?: number;
	shouldVindicate?: boolean;
	mailsSent?: boolean;
	navigatorId?: number;
	ourCompany?: string;
	amountMin?: number;
	amountMax?: number;
	amountWithVatMin?: number;
	amountWithVatMax?: number;
	currency?: string;
	vatMin?: number;
	vatMax?: number;
	daysAfterDeadlineMin?: number;
	daysAfterDeadlineMax?: number;
	deferredPaymentDaysMin?: number;
	deferredPaymentDaysMax?: number;
	carrierReference?: string;
}

const FILTER_PARAM_MAP: Record<keyof InvoiceFiltersFormValue, string> = {
	mail: 'contractor_mail',
	nip: 'nip',
	contractorId: 'contractor',
	number: 'number',
	paymentDeadlineFrom: 'payment_deadline_after',
	paymentDeadlineTo: 'payment_deadline_before',
	paymentTimeFrom: 'payment_time_after',
	paymentTimeTo: 'payment_time_before',
	declaredPaymentDateFrom: 'declared_payment_date_after',
	declaredPaymentDateTo: 'declared_payment_date_before',
	issueDate: 'issue_date',
	status: 'status',
	internalStatus: 'internal_status',
	issuedBy: 'issued_by',
	shouldVindicate: 'should_vindicate',
	mailsSent: 'is_any_mail_sent',
	navigatorId: 'navigator_id',
	ourCompany: 'our_company',
	amountMin: 'amount_min',
	amountMax: 'amount_max',
	amountWithVatMin: 'amount_with_vat_min',
	amountWithVatMax: 'amount_with_vat_max',
	currency: 'currency__iexact',
	vatMin: 'vat_min',
	vatMax: 'vat_max',
	daysAfterDeadlineMin: 'days_after_deadline_min',
	daysAfterDeadlineMax: 'days_after_deadline_max',
	deferredPaymentDaysMin: 'deferred_payment_days_min',
	deferredPaymentDaysMax: 'deferred_payment_days_max',
	carrierReference: 'carrier_reference',
};

@Component({
	selector: 'app-invoice-filters',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatInputModule,
		MatSelectModule,
		MatButtonModule,
		MatDateRangeInput,
		MatDateRangePicker,
		MatDatepickerToggle,
		MatStartDate,
		MatEndDate,
		GenericMatAutocompleteComponent,
		MatDatepickerModule,
	],
	templateUrl: './invoice-filters.component.html',
	styleUrl: './invoice-filters.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
})
export class InvoiceFiltersComponent implements OnInit {
	readonly statusVindicationFilters = STATUS_VINDICATION_FILTERS;
	readonly internalStatusOptions = input<InternalStatusDto[]>([]);
	readonly issuedByOptions = input<IssuedByOption[]>([]);

	readonly showFiltersBtnClick = output<string>();

	readonly contractorsApiUrl = `${environment.debtCollectionUrl}/contractors/`;

	readonly invoiceFilterForm = inject(FormBuilder).group({
		mail: [''],
		nip: [''],
		contractorId: [null],
		number: [''],
		paymentDeadlineFrom: [null],
		paymentDeadlineTo: [null],
		paymentTimeFrom: [null],
		paymentTimeTo: [null],
		declaredPaymentDateFrom: [null],
		declaredPaymentDateTo: [null],
		issueDate: [null],
		status: [null],
		internalStatus: [[] as number[]],
		issuedBy: [null],
		shouldVindicate: [null],
		mailsSent: [null],
		navigatorId: [null],
		ourCompany: [null],
		amountMin: [null],
		amountMax: [null],
		amountWithVatMin: [null],
		amountWithVatMax: [null],
		currency: [''],
		vatMin: [null],
		vatMax: [null],
		daysAfterDeadlineMin: [null],
		daysAfterDeadlineMax: [null],
		deferredPaymentDaysMin: [null],
		deferredPaymentDaysMax: [null],
		carrierReference: [''],
	});

	private readonly datePipe = inject(DatePipe);
	private readonly dateAdapter = inject(DateAdapter);

	contractorsResponseMapper = (response: Result<Contractor>): Contractor[] => {
		return response?.results ?? [];
	};

	ngOnInit(): void {
		this.dateAdapter.setLocale('pl-PL');
	}

	showInvoiceFilters(): void {
		this.invoiceFilterForm.markAllAsTouched();
		if (this.invoiceFilterForm.invalid) {
			return;
		}
		const filtersParams = this.buildFiltersParams(this.invoiceFilterForm.value);
		this.showFiltersBtnClick.emit(filtersParams);
	}

	onEnterKey(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.showInvoiceFilters();
		}
	}

	resetFilters(): void {
		this.invoiceFilterForm.reset();
	}

	private formatDateValue(date: Date): string | null {
		return this.datePipe.transform(date, 'yyyy-MM-dd');
	}

	private formatFilterValue(value: Date | string | number | boolean): string | null {
		if (value instanceof Date) {
			return this.formatDateValue(value);
		}
		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			return String(value);
		}
		return null;
	}

	private buildFiltersParams(filters: InvoiceFiltersFormValue): string {
		const params = new URLSearchParams();

		Object.entries(filters).forEach(([key, value]) => {
			if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
				return;
			}

			const paramName = FILTER_PARAM_MAP[key as keyof InvoiceFiltersFormValue];
			if (!paramName) {
				return;
			}

			if (Array.isArray(value)) {
				value.forEach((arrayValue) => {
					const formattedArrayValue = this.formatFilterValue(arrayValue);
					if (formattedArrayValue !== null) {
						params.append(paramName, formattedArrayValue);
					}
				});
				return;
			}

			const formattedValue = this.formatFilterValue(value);
			if (formattedValue !== null) {
				params.append(paramName, formattedValue);
			}
		});

		const paramsString = params.toString();
		return paramsString ? `&${paramsString}` : '';
	}
}
